
import React from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { Typography } from "@mui/material";
import MDBox from "components/MDBox";
import ChartHeader from "examples/Charts/General/ChartHeader";
import { motion } from "framer-motion";
import ExecutiveSummaryProfileCard from "./ExecutiveSummaryProfileCard";
import ChartAssistant from "components/ChartAssistant";

function ExecutiveSummary({
  title = "Executive Summary",
  description = null,
  is_derived = false,
  datasets,
  previousdatasets,
  adjustedcurrentdatasets,
  adjustedpreviousdatasets,
  futuredatasets = [],
  isPredictionEnabled = false,
  isLoading = false,
  selectedCategories = [],
  entitlements // ✅ Added prop
}) {
  if (isLoading) {
    return (
      <Card>
        <MDBox
          p={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="250px"
        >
          <CircularProgress size={32} thickness={4} color="accent" />
        </MDBox>
      </Card>
    );
  }

  if (!datasets || datasets.length === 0) {
    return (
      <Card>
        <MDBox p={3} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            No profile data available for Executive Summary.
          </Typography>
        </MDBox>
      </Card>
    );
  }

  // ---------------------------------------------------
  // ⭐ NEW LOGIC: GLOBAL HIDE FLAG
  // ---------------------------------------------------
  const hideKeyMetrics = datasets.some((p) => p.own === false);
  // ---------------------------------------------------

  const tooltipContent = (
    <div style={{ maxWidth: "380px", textAlign: "left" }}>
      <Typography variant="body2" gutterBottom>
        <strong>Description:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        The Executive Summary provides a snapshot of each selected profile’s
        performance, highlighting audience size, engagement, and comparative
        growth within the selected period.
      </Typography>
      <Typography variant="body2" gutterBottom>
        <strong>Insights you can gain:</strong>
      </Typography>
      <Typography variant="caption" display="block">
        - Identify which profiles are leading in audience, engagement, or content growth.
        <br />
        - Compare recent performance with the previous period.
        <br />
        - Use this as a summary before deep-dive charts.
      </Typography>
    </div>
  );

  const profilePictures = datasets.map(
    (p) => p.profilePictureUrl || "https://via.placeholder.com/150"
  );

  const shouldShowSummary =
    selectedCategories?.includes("Audience") ||
    selectedCategories?.includes("Engagement") ||
    selectedCategories?.includes("Content") ||
    selectedCategories?.includes("Demographics"); // ✅ Added Demographics

  const totalProfiles = datasets.length;

  const findPreviousProfile = (currentProfile) => {
    if (!previousdatasets || previousdatasets.length === 0) return null;

    const byId = previousdatasets.find(
      (prev) => prev.profileId === currentProfile.profileId
    );
    if (byId) return byId;

    return previousdatasets.find(
      (prev) =>
        prev.label?.toLowerCase().trim() ===
        currentProfile.label?.toLowerCase().trim()
    );
  };

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        pb: 2,
        overflow: "visible",
        position: "relative", // Ensure ChartAssistant is positioned relative to this card
      }}
    >
      <ChartHeader
        title={title || "Executive Summary"}
        description={description || tooltipContent}
        profilePictures={profilePictures}
        is_derived={is_derived}
      >
        <ChartAssistant
          title="Executive Summary"
          context="Executive Summary"
          chartData={{
            datasets,
            previousdatasets,
            selectedCategories
          }}
          rawData={datasets}
          sx={{
            position: "static",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)", // Keep subtle shadow
            ml: 2
          }}
          entitlements={entitlements} // ✅ Pass entitlements
        />
      </ChartHeader>

      <MDBox
        component={motion.div}
        layout="position"
        transition={{
          duration: 0.35,
          ease: [0.25, 0.8, 0.25, 1],
        }}
        px={3}
        py={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          backgroundColor: "transparent",
          width: "100%",
          margin: "0 auto",
          overflow: "visible",
        }}
      >
        {shouldShowSummary ? (
          <Grid container spacing={3} justifyContent="center" alignItems="stretch">
            {datasets.map((profile) => {
              const prevProfileData = findPreviousProfile(profile);
              const isSingleProfile = totalProfiles === 1;
              const colSize = isSingleProfile ? 12 : 6;

              return (
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={colSize}
                  lg={colSize}
                  key={profile.profileId || profile.label}
                  sx={{ display: "flex", justifyContent: "center" }}
                >
                  <ExecutiveSummaryProfileCard
                    profile={profile}
                    datasets={datasets}
                    previousdatasets={previousdatasets}
                    adjustedcurrentdatasets={adjustedcurrentdatasets}
                    adjustedpreviousdatasets={adjustedpreviousdatasets}
                    selectedCategories={selectedCategories}

                    // ⭐ NEW PROP HERE
                    hideKeyMetrics={hideKeyMetrics}
                  />
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <MDBox p={3} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              Select a chart category to view its summary.
            </Typography>
          </MDBox>
        )}
      </MDBox>
    </Card >
  );
}

ExecutiveSummary.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  is_derived: PropTypes.bool,
  datasets: PropTypes.array.isRequired,
  previousdatasets: PropTypes.array.isRequired,
  futuredatasets: PropTypes.array,
  adjustedcurrentdatasets: PropTypes.array.isRequired,
  adjustedpreviousdatasets: PropTypes.array.isRequired,
  isPredictionEnabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  selectedCategories: PropTypes.array,
  entitlements: PropTypes.object, // ✅ Added propType
};

export default ExecutiveSummary;
